import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { postStakeholderIdentify } from '../services/api';

// US-stakeholder-identify (2026-05-04)
//
// Non-dismissable modal that blocks the owner-side UI until the human
// behind the shared stakeholder login types in their name and self-
// described role (e.g. "John Smith", "CFO" / "Board Member"). On submit,
// posts to /auth/stakeholder/identify which writes the pair into
// AuditLog as a 'stakeholder_session_identified' event.
//
// Mounted in App.js so it overlays every owner-side route. Renders only
// when AuthContext.needsIdentification is true (stakeholder + not yet
// identified in this tab session).
export default function IdentifyMeModal() {
  const { needsIdentification, setIdentified } = useAuth();
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (!needsIdentification) return null;

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    const trimmedName = name.trim();
    const trimmedRole = role.trim();
    if (!trimmedName) {
      setError('Please enter your name.');
      return;
    }
    if (!trimmedRole) {
      setError('Please enter your role.');
      return;
    }

    setSubmitting(true);
    try {
      await postStakeholderIdentify({ name: trimmedName, role: trimmedRole });
      setIdentified({
        name: trimmedName,
        role: trimmedRole,
        identified_at: new Date().toISOString()
      });
    } catch (err) {
      setError(err.message || "Something went wrong — we're looking into it.");
      setSubmitting(false);
    }
  }

  return (
    <div style={styles.overlay} role="dialog" aria-modal="true" aria-labelledby="identifyme-title">
      <div style={styles.card}>
        <h2 id="identifyme-title" style={styles.title}>Welcome to the Alloggio Owner Portal</h2>
        <p style={styles.subtitle}>
          This is a shared preview login. So we know who's looking, please tell us your name and role.
        </p>

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.field}>
            <label style={styles.label} htmlFor="identifyme-name">Name</label>
            <input
              id="identifyme-name"
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              style={styles.input}
              placeholder="e.g. John Smith"
              required
              autoFocus
              disabled={submitting}
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label} htmlFor="identifyme-role">Role</label>
            <input
              id="identifyme-role"
              type="text"
              value={role}
              onChange={e => setRole(e.target.value)}
              style={styles.input}
              placeholder="e.g. CFO, Board Member, Investor"
              required
              disabled={submitting}
            />
          </div>

          {error && <div style={styles.error}>{error}</div>}

          <button
            type="submit"
            style={{ ...styles.button, opacity: submitting ? 0.7 : 1 }}
            disabled={submitting}
          >
            {submitting ? 'Continuing...' : 'Continue'}
          </button>
        </form>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(19, 33, 57, 0.55)',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '20px'
  },
  card: {
    backgroundColor: 'var(--white, #ffffff)',
    borderRadius: '12px',
    padding: '36px 36px',
    width: '100%',
    maxWidth: '440px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
    border: '1px solid var(--gray-100, #e5e7eb)'
  },
  title: {
    fontSize: '20px',
    fontWeight: 700,
    color: 'var(--navy, #132139)',
    margin: '0 0 8px 0',
    letterSpacing: '-0.01em'
  },
  subtitle: {
    fontSize: '14px',
    color: 'var(--gray-600, #4b5563)',
    margin: '0 0 22px 0',
    lineHeight: 1.5
  },
  form: { display: 'flex', flexDirection: 'column', gap: '16px' },
  field: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: {
    fontSize: '12px',
    fontWeight: 500,
    color: 'var(--gray-800, #1f2937)',
    textTransform: 'uppercase',
    letterSpacing: '0.06em'
  },
  input: {
    padding: '11px 14px',
    borderRadius: '8px',
    border: '1px solid var(--gray-200, #e5e7eb)',
    fontSize: '14px',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box'
  },
  error: {
    backgroundColor: 'var(--red-light, #fef2f2)',
    border: '1px solid var(--red, #dc2626)',
    borderRadius: '8px',
    padding: '11px 14px',
    fontSize: '13px',
    color: 'var(--red, #dc2626)'
  },
  button: {
    backgroundColor: 'var(--navy, #132139)',
    color: 'var(--white, #ffffff)',
    border: 'none',
    borderRadius: '6px',
    padding: '12px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    width: '100%',
    letterSpacing: '0.01em',
    marginTop: '4px',
    boxShadow: '0 2px 8px rgba(19,33,57,0.22)'
  }
};
